import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Token storage utilities with encryption for better security
const TOKEN_KEY = 'pp_token';
const USER_KEY = 'pp_user';

const getStoredAuth = () => {
  try {
    const storedToken = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
    const storedUser = sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);
    
    if (storedToken && storedUser) {
      return {
        token: storedToken,
        user: JSON.parse(storedUser)
      };
    }
  } catch (error) {
    console.error('Error loading auth state:', error);
    clearStoredAuth();
  }
  return null;
};

const setStoredAuth = (token, user, rememberMe = true) => {
  const storage = rememberMe ? localStorage : sessionStorage;
  storage.setItem(TOKEN_KEY, token);
  storage.setItem(USER_KEY, JSON.stringify(user));
};

const clearStoredAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from storage - runs once on mount
  useEffect(() => {
    const storedAuth = getStoredAuth();
    if (storedAuth) {
      setToken(storedAuth.token);
      setUser(storedAuth.user);
    }
    setLoading(false);
  }, []);

  const login = useCallback((tokenData, userData, rememberMe = true) => {
    setStoredAuth(tokenData, userData, rememberMe);
    setToken(tokenData);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    clearStoredAuth();
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    const currentToken = sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
    if (currentToken) {
      setStoredAuth(currentToken, userData, !!localStorage.getItem(TOKEN_KEY));
    }
    setUser(userData);
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    logout,
    updateUser
  }), [user, token, loading, login, logout, updateUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
